import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { Tooltip } from "src/ui/shared/Tooltip";

import { RoleLabel } from "@dashboard/shared/components/RoleLabel";
import { getAssignableRoleOptions, rolesOptions } from "@dashboard/shared/roles";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { PermissionService } from "@services/permissionService/permissionService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { showSuccessToast } from "@shared/toasts/Toasts";

function validateRole(role: unknown) {
    if (role !== "Owner" && role !== "Manager" && role !== "User") {
        throw new Error(i18n.t("pages:workspacePage.toast.invalidRole"));
    }
    return role;
}

type Props = {
    member: WorkspaceMember;
};

export const ChangeWorkspaceRoleField = (props: Props) => {
    const [loading, setLoading] = React.useState(false);
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();
    const { t } = useTranslation(["common", "pages", "modals", "sidepanels"]);

    const isExpired = useCurrentSubscriptionExpired();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace } = useCurrentWorkspace();

    const { workspaceAbility } = PermissionService();
    const ability = workspaceAbility(workspace);
    const canManageMembers = ability.can("manage", "Member");
    const canAssignOwner = ability.can("manage", "Owner");

    // A Manager may neither change an owner's role nor assign the owner role (mirrors the backend permission checks).
    const mayManageMember = canManageMembers && (canAssignOwner || props.member.role !== "Owner");
    const options = getAssignableRoleOptions(canAssignOwner);

    const onRoleChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        if (!workspaceId) {
            return;
        }

        setLoading(true);
        const role = validateRole(selectedOption.value);
        await updateWorkspaceMembers({
            id: workspaceId,
            changedMembers: { [props.member.email]: role },
        })
            .unwrap()
            .then(() => showSuccessToast(t("pages:workspacePage.toast.membersUpdateSuccess")))
            .finally(() => setLoading(false));
    };

    const getTooltip = () => {
        if (!mayManageMember) {
            return t("sidepanels:groupPanel.tooltip.cannotEditOwnerRole");
        }
        if (isExpired) {
            return t("pages:workspacePage.tooltip.subscriptionExpired");
        }
        return null;
    };

    const tooltip = getTooltip();

    // A member who cannot manage members can never edit roles, so show the role as plain text.
    if (!canManageMembers) {
        return <RoleLabel role={props.member.role} />;
    }

    return (
        <Tooltip title={tooltip} disableChildrenWrapper>
            <SelectField
                value={rolesOptions.find((option) => option.value === props.member.role)}
                options={options}
                onChange={onRoleChange}
                disabled={loading || !!tooltip}
                fullWidth
                disableSorting
            />
        </Tooltip>
    );
};
